<style lang="scss" scope>
th {
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: $gray-200;
  }

  &.not-sortable {
    cursor: inherit;

    &:hover {
      background-color: inherit;
    }
  }
}

.table-loading {
  min-height: 250px;
}

.category {
  text-transform: uppercase;
}

.table-wrapper {
  overflow-x: auto;
  overflow-y: hidden;
}

.updating-icon {
  right: 15px;
  top: 15px;
}

.list-complete-item {
  transition: all 0s;
}
.list-complete-enter {
  opacity: 0;
}
</style>

<template>
  <section v-if="loading" class="card card-table">
    <div class="card-header">
      <div
        class="table-loading d-flex justify-content-center align-items-center"
      >
        <i class="fas fa-sync fa-7x fa-spin"></i>
      </div>
    </div>
  </section>
  <section v-else class="card card-table">
    <div v-if="hasTitleSlot" class="card-header">
      <div class="d-flex mb-2">
        <h4>
          <slot name="title"></slot>
          <i
            v-if="updating"
            class="updating-icon position-absolute fas fa-sync fa fa-spin"
          ></i>
        </h4>
      </div>
    </div>
    <div class="card-body">
      <div class="m-0 p-0">
        <slot name="filter"></slot>
      </div>
      <div class="table-wrapper">
        <table class="table">
          <thead>
            <tr>
              <th
                :class="{
                  'not-sortable': attribute.sortable === false,
                  'text-center': attribute.centered
                }"
                @click="sortBy(attribute)"
                v-for="(attribute, index) in attributes"
                :key="index"
                v-tooltip:top="attribute.tooltip ? attribute.tooltip : ''"
              >
                <span v-if="attribute.html" v-html="attribute.name"></span>
                <span v-else>{{ attribute.name }}</span>
                <span v-if="sort === attribute.attribute" class="fa-stack">
                  <i
                    v-if="sortMode === 'ASC'"
                    class="fas fa-sort-up fa-stack-1x"
                  ></i>
                  <i
                    v-if="sortMode === 'DESC'"
                    class="fas fa-sort-down fa-stack-1x"
                  ></i>
                </span>
              </th>
            </tr>
          </thead>
          <slot v-if="hasTopSlot" name="top"></slot>
          <transition-group name="list-complete" tag="tbody">
            <tr
              v-for="(model, index) in sortedModels"
              :class="cssClass(model)"
              class="list-complete-item"
              :key="createIndex(model, index)"
            >
              <component
                v-for="(attribute, attributeIndex) in attributes"
                :is="component(attribute)"
                :content="
                  content(
                    model,
                    attribute.attribute,
                    attribute.displayedAttribute
                  )
                "
                :model="model"
                :key="attributeIndex"
                :index="index"
                :label="attribute.label"
                :disabled="attribute.disabled"
                :attribute="attribute"
                :tableId="id"
                :style="attribute.customStyle"
                @table-update="refresh"
                @removeModel="removeModel(model.id)"
              ></component>
            </tr>
          </transition-group>

          <slot v-if="hasBottomSlot" name="bottom"></slot>
        </table>
      </div>
      <div class="row" v-if="hasPagination">
        <div class="col-sm-6 pagination-info">
          <p class="category">Zeigt {{ from }} bis {{ to }} von {{ count }}</p>
        </div>
        <div class="col-sm-6">
          <ul class="pagination pull-right pagination-default">
            <li
              class="page-item prev-page"
              @click="previousPage()"
              :class="{ disabled: loadedPage === 1 }"
            >
              <a aria-label="Previous" class="page-link">
                <span aria-hidden="true">
                  <i aria-hidden="true" class="fa fa-angle-double-left"></i>
                </span>
              </a>
            </li>
            <li
              v-for="page in pages"
              :key="page"
              class="page-item"
              :class="{ active: page === loadedPage }"
            >
              <a
                v-if="page !== null"
                class="page-link"
                @click="paginateTo(page)"
                >{{ page }}</a
              >
              <span v-else class="page-link">...</span>
            </li>
            <li
              class="page-item next-page"
              @click="nextPage()"
              :class="{ disabled: loadedPage === maxPages }"
            >
              <a aria-label="Next" class="page-link">
                <span aria-hidden="true">
                  <i aria-hidden="true" class="fa fa-angle-double-right"></i>
                </span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="babel">
import { unionBy, range } from "lodash";

import TextCell from "@/components/table/TextCell.vue";
import ShortenTextCell from "@/components/table/ShortenTextCell.vue";
import SexCell from "@/components/table/SexCell.vue";
import ActionCell from "@/components/table/ActionCell.vue";
import DeleteCell from '@/components/table/DeleteCell.vue';
import MediaCell from "@/components/table/MediaCell.vue";
import DateCell from "@/components/table/DateCell.vue";
import DateTimeCell from "@/components/table/DateTimeCell.vue";
import CurrencyCell from '@/components/table/CurrencyCell.vue';
import OnlineCell from "@/components/table/OnlineCell.vue";
import ProfileOnlineCell from "@/components/table/ProfileOnlineCell.vue";
import CheckMarkerCell from "@/components/table/CheckMarkerCell.vue";
import CopyClipboardCell from '@/components/table/CopyClipboardCell.vue';
import CustomerActionCell from '@/components/table/CustomerActionCell.vue';
import CustomerMediaConfirmCell from "@/components/table/CustomerMediaConfirmCell.vue";
import AgencyModeratorCell from "@/components/table/AgencyModeratorCell.vue";
import ImageCell from '@/components/table/ImageCell.vue';
import MultiSexCell from '@/components/table/MultiSexCell.vue';
import MessagePoolCell from '@/components/table/MessagePoolCell.vue';
import ProfileCell from '@/components/table/ProfileCell.vue';
import RadioButtonCell from '@/components/table/RadioButtonCell.vue';
import GiftImageCell from '@/components/table/GiftImageCell.vue';
import TransactionConfirmCell from '@/components/table/TransactionConfirmCell.vue';
import UserCell from '@/components/table/UserCell.vue';
import MinutesSinceCell from '@/components/table/MinutesSinceCell.vue';
import ModalCell from '@/components/table/ModalCell.vue';
import CouponActionCell from '@/components/table/CouponActionCell';
import DialogLink from '@/components/table/DialogLink.vue';
import InOutCell from '@/components/table/InOutCell.vue';
import InOrOutCell from '@/components/table/InOrOutCell.vue';
import LastPageCell from '@/components/table/LastPageCell.vue';
import LinkFriendCell from '@/components/table/LinkFriendCell.vue';
import LinkProfileCell from '@/components/table/LinkProfileCell.vue';
import DeleteFriendCell from '@/components/table/DeleteFriendCell.vue';
import CustomerOriginCell from '@/components/table/CustomerOriginCell.vue';
import BouncedEmailActionCell from '@/components/table/BouncedEmailActionCell.vue';
import ToggleCell from '@/components/table/ToggleCell.vue';
import CountdownCell from '@/components/table/CountdownCell.vue';
import DoubleValueCell from '@/components/table/DoubleValueCell.vue';
import DoubleValuePercentageCell from '@/components/table/DoubleValuePercentageCell.vue';
import CircularMailActionCell from '@/components/table/CircularMailActionCell.vue';
import CircularMailStatusCell from '@/components/table/CircularMailStatusCell.vue';
import CircularMailReadCell from '@/components/table/CircularMailReadCell.vue';
import SupportTicketBrowserInfoCell from '@/components/table/SupportTicketBrowserInfoCell.vue';
import UserMessageCountCell from '@/components/table/UserMessageCountCell.vue';
import ClickableIdCell from '@/components/table/ClickableIdCell.vue';
import ExpandableIdCell from '@/components/table/ExpandableIdCell.vue';
import URLExamplesCell from '@/components/table/URLExamplesCell.vue';
import TakeDialogCell from '@/components/table/TakeDialogCell.vue';
import CopyPublicLinkCell from '@/components/table/CopyPublicLinkCell.vue';
import ModeratorActionCell from '@/components/table/ModeratorActionCell';
import ServiceStringCell from '@/components/table/ServiceStringCell.vue';
import ModeratorEfficencyCell from '@/components/table/ModeratorEfficencyCell.vue';
import ModeratorInformationActionCell from '@/components/table/ModeratorInformationActionCell.vue';
import WaitingDialogActionCell from '@/components/table/WaitingDialogActionCell.vue';
import WaitingroomDialogActionCell from '@/components/table/WaitingroomDialogActionCell.vue';
import IpCell from '@/components/table/IpCell.vue';
import FlagCell from '@/components/table/FlagCell.vue';
import TimeCounterCell from '@/components/table/TimeCounterCell.vue';
import OnlineVerifiedCell from '@/components/table/OnlineVerifiedCell.vue';
import BalanceCell from '@/components/table/BalanceCell.vue';
import CustomerCell from '@/components/table/CustomerCell.vue';
import UserNoEmailCell from '@/components/table/UserNoEmailCell.vue';
import AgencyUserNoEmailCell from '@/components/table/AgencyUserNoEmailCell.vue';
import MessageContentCell from '@/components/table/MessageContentCell.vue';
import TransactionLinkCell from '@/components/table/TransactionLinkCell.vue';
import FakeActionCell from '@/components/table/FakeActionCell.vue';
import AffiliateActionCell from '@/components/table/AffiliateActionCell.vue';
import SupportReadByCell from '@/components/table/SupportReadByCell.vue';
import SupportShortendMessageCell from '@/components/table/SupportShortendMessageCell.vue';
import DialogInfoCell from '@/components/table/DialogInfoCell.vue';
import ManualTransactionCell from '@/components/table/ManualTransactionCell.vue';
import PostbackActionCell from '@/components/table/PostbackActionCell.vue';
import PostbackDataCell from '@/components/table/PostbackDataCell.vue';
import ExternRegistrationActionCell from '@/components/table/ExternRegistrationActionCell.vue';
import EditAgencyNameModalCell from '@/components/table/EditAgencyNameModalCell.vue';
import CustomerProfileDescriptionActionCell from '@/components/table/CustomerProfileDescriptionActionCell.vue';
import CustomerReviewContentCell from '@/components/table/CustomerReviewContentCell.vue';
import CustomerReviewActionCell from '@/components/table/CustomerReviewActionCell.vue';
import RolesCell from '@/components/table/RolesCell.vue';
import PermissionsCell from '@/components/table/PermissionsCell.vue';
import SystemUserActionCell from '@/components/table/SystemUserActionCell.vue';
import SystemUserNameCell from '@/components/table/SystemUserNameCell.vue';
import CustomerOriginAndIpCell from '@/components/table/CustomerOriginAndIpCell.vue';
import DoubleValueThousendSeperatCell from '@/components/table/DoubleValueThousendSeperatCell.vue';
import InputCell from '@/components/table/InputCell.vue';
import UpdateCell from '@/components/table/UpdateCell.vue';
import EMailLogToCell from '@/components/table/EMailLogToCell.vue';
import PingCell from '@/components/table/PingCell.vue';
import CustomerInOutCell from '@/components/table/CustomerInOutCell.vue';

export default {
  components: {
    TextCell,
    ShortenTextCell,
    SexCell,
    ActionCell,
    DeleteCell,
    CheckMarkerCell,
    CopyClipboardCell,
    MediaCell,
    DateCell,
    DateTimeCell,
    CurrencyCell,
    CustomerActionCell,
    CustomerMediaConfirmCell,
    LastPageCell,
    AgencyModeratorCell,
    OnlineCell,
    ProfileOnlineCell,
    ImageCell,
    MultiSexCell,
    MessagePoolCell,
    ProfileCell,
    RadioButtonCell,
    GiftImageCell,
    TransactionConfirmCell,
    UserCell,
    MinutesSinceCell,
    ModalCell,
    CouponActionCell,
    DialogLink,
    InOutCell,
    LinkFriendCell,
    LinkProfileCell,
    DeleteFriendCell,
    InOrOutCell,
    CustomerOriginCell,
    BouncedEmailActionCell,
    ToggleCell,
    CountdownCell,
    DoubleValueCell,
    DoubleValuePercentageCell,
    CircularMailActionCell,
    CircularMailStatusCell,
    CircularMailReadCell,
    URLExamplesCell,
    CopyPublicLinkCell,
    SupportTicketBrowserInfoCell,
    UserMessageCountCell,
    ClickableIdCell,
    ExpandableIdCell,
    TakeDialogCell,
    ModeratorActionCell,
    ServiceStringCell,
    ModeratorEfficencyCell,
    ModeratorInformationActionCell,
    WaitingDialogActionCell,
    WaitingroomDialogActionCell,
    IpCell,
    FlagCell,
    TimeCounterCell,
    OnlineVerifiedCell,
    BalanceCell,
    CustomerCell,
    UserNoEmailCell,
    AgencyUserNoEmailCell,
    MessageContentCell,
    TransactionLinkCell,
    FakeActionCell,
    AffiliateActionCell,
    SupportReadByCell,
    SupportShortendMessageCell,
    DialogInfoCell,
    ManualTransactionCell,
    PostbackActionCell,
    PostbackDataCell,
    ExternRegistrationActionCell,
    EditAgencyNameModalCell,
    CustomerProfileDescriptionActionCell,
    CustomerReviewContentCell,
    CustomerReviewActionCell,
    RolesCell,
    PermissionsCell,
    SystemUserActionCell,
    SystemUserNameCell,
    CustomerOriginAndIpCell,
    DoubleValueThousendSeperatCell,
    InputCell,
    UpdateCell,
    EMailLogToCell,
    PingCell,
    CustomerInOutCell
  },
  created () {
    if (this.$route && this.$route.query.table) {
      let table = JSON.parse(this.$route.query.table);
      if (table.filter) {
        let thisFilter = this.filter;
        let filter = [];
        this.loadedPage = table.page;
        table.filter.forEach(function(value) {
          if (! thisFilter.find(filter => JSON.stringify(filter) == JSON.stringify(value))) {
            filter.unshift(value);
          }
        });
        if (filter.findIndex(value => value.name == "skip") >= 0) {
          filter.splice(
            filter.findIndex(value => value.name == "skip")
            ,1
          );
        }
        filter.push({"name": "skip", "value": table.take * (table.page - 1)});
        filter.forEach(value => this.filter.unshift(value));
      }
    }

    this.$bus.$on('refresh-table', (id) => {
      if (id && id !== this.id) {
        return;
      }
      this.storePage(1, this.take, this.filter);

      const oldId = this.id;
      this.updating = true;
      this.fetch().finally(() => {
        this.$store.commit('table/delete', oldId);
        this.updating = false;
      });
    });
    if (this.fetchOnQueue) {
      this.$bus.$on('table-data-fetched', (id) => {
        if (id && id !== this.id) {
          return;
        }
        this.loading = false;
      });
    } else if (!this.fetchAfterCreate) {
      this.fetch().finally(() => this.loading = false);
    } else {
      this.loading = false;
    }
  },
  data() {
    return {
      search: '',
      sort: null,
      id: null,
      loadedPage: 1,
      loading: true,
      updating: false,
      sortMode: 'DESC',
      paths: [
        "/zahlungen/vorgaenge",
        "/mitglieder/list",
        "/moderator/list",
        "/fake/list",
        "/mitglieder/support"
      ]
    }
  },
  props: {
    fetchAfterCreate: {
      type: Boolean,
      default: false
    },
    fetchOnQueue: {
      type: Boolean,
      default: false
    },
    url: {
      type: String,
      required: true,
    },
    filter: {
      type: Array,
      default: () => [],
    },
    appends: {
      type: Array,
      default: () => [],
    },
    additionalRequestData: {
      type: Object,
      default: () => {},
    },
    rowClasses: {
      type: Object,
      default: () => {},
    },
    attributes: {
      required: true,
      type: Array,
    },
    name: {
      type: String,
      default: null
    },
    counter: {
      type: Number,
      default: 0
    }
  },
  computed: {
    hasTitleSlot() {
      return Boolean(this.$slots.title);
    },
    hasBottomSlot() {
      return Boolean(this.$slots.bottom);
    },
    hasTopSlot() {
      return Boolean(this.$slots.top);
    },
    sortedModels() {
      return this.sort === null ? this.models : this.models.concat().sort(this.customSort);
    },
    table() {
      return this.$store.getters['table/find'](this.id);
    },
    models() {
      if (this.hasTable) {
        return Array.isArray(this.table.models) ? this.table.models : [this.table.models];
      }

      return [];
    },
    hasTable() {
      return this.table !== undefined;
    },
    hasPagination() {
      return this.hasTable ? this.table.count > this.models.length : false;
    },
    take() {
      return this.hasTable ? this.$store.getters['table/take'](this.id) : 0;
    },
    skip() {
      return this.hasTable ? this.$store.getters['table/skip'](this.id): 0;
    },
    count() {
      return this.hasTable ? this.table.count : 0;
    },
    from() {
      return this.skip + 1;
    },
    to() {
      let to = this.from + this.take - 1;

      if (to > this.count) {
        to = this.count;
      }

      return to;
    },
    pages() {
      if (this.maxPages < 11) {
        return range(1, this.maxPages + 1);
      } else {
        if (this.loadedPage < 5 || this.loadedPage > this.maxPages-4) {
          return [...range(1, 6), null, ...range(this.maxPages -5, this.maxPages+1)];
        } else {
          return [...range(1, 3), null, ...range(this.loadedPage-2, this.loadedPage+3), null, ...range(this.maxPages -1, this.maxPages+1)];
        }
      }
    },
    maxPages() {
      return Math.ceil(this.count/this.take);
    },
    sortFilter() {
      return {
        name: "sort",
        mode: this.sortMode,
        attribute: this.sort === null ? "created_at" : this.sort,
      };
    },
  },
  methods: {
    storePage(page, take, filter) {
      if (this.$route && this.paths.includes(this.$route.path)) {
        filter = JSON.stringify(filter);
        let newFilter = '{"page":' + page + ',"take":' + take + ',"filter": ' + filter + '}';
        if (this.$route.query.table != newFilter) {
          this.$router.push({
            query: {
              table: newFilter
            }
          });
        }
      }
    },
    createIndex(model,index) {
      return  model.id ? 'row-' + model.id : 'row-'+ index;
    },
    cssClass (model) {
      if (!this.rowClasses) {
        return [];
      }

      const keys = Object.keys(this.rowClasses);
      return keys.reduce((classes, key) => {
        if (this.rowClasses[key](model)) {
          classes.push(key);
        }
        return classes;
      }, []);
    },
    fetch() {
      return this.$store.dispatch("table/new", {
        url: this.url,
        filter: this.filter,
        appends: this.appends,
        name: this.name,
        additionalRequestData: this.additionalRequestData,
        fetchOnQueue: this.fetchOnQueue
      }).then( id => {
        this.id = id;
        this.$bus.$emit('table-fetched');
        this.$emit('table-fetched');
      });
    },
    refresh(data) {
      return this.$store.dispatch('table/update', this.table).then(() => {
        if (data && data.count) {
          this.$store.dispatch('table/count', this.table);
        }
      });
    },
    removeModel(modelId) {
      this.$store.commit("table/removeModel", {
        tableId: this.table.id,
        modelId
      })
    },
    content(model, path, displayedAttribute) {
      if (path === undefined) {
        return "";
      }

      if (displayedAttribute) {
        return model[displayedAttribute];
      }

      if (typeof path === 'function') {
        path = path(model);
      }

      const segments = path.split('.');

      let value = model;
      segments.forEach((segment) => {
        if (segment.endsWith(']')) {
          let indexValue = segment.substring(segment.indexOf('[') + 1, segment.length - 1);
          segment = segment.substring(0, segment.indexOf('['));

          if (value[segment].length > 0) {
            value = value[segment][indexValue];
          } else {
            value = null;
          }
        } else {
          if (value !== null) {
            value = value[segment];
          }
        }
      });
      return value;
    },
    component({type}) {
        return Object.keys(this.$options.components).includes(type) ?
          type : 'TextCell';
    },
    nextPage() {
      if (this.loadedPage < this.maxPages) {
        this.paginateTo(this.loadedPage+1);
      }
    },
    previousPage() {
      if (this.loadedPage > 1) {
        this.paginateTo(this.loadedPage-1);
      }
    },
    paginateTo(page) {
      const table = Object.assign({}, this.table);
      table.filter = unionBy([{
        name: 'skip',
        value: (page-1)*this.take,
      }], this.table.filter, "name");
      this.$store.dispatch('table/update', table).then(() => {
        this.loadedPage = page;
        this.storePage(this.loadedPage, this.take, this.filter);
      });
    },
    customSort (modelA, modelB) {
      const valueA = modelA[this.sort];
      const valueB = modelB[this.sort];
      if(Number(valueA) < Number(valueB)) {
        return this.sortMode === "DESC" ? 1 : -1;
      } else if (Number(valueA) > Number(valueB)) {
        return this.sortMode === "DESC" ? -1 : 1;
      }
      return 0;
    },
    sortBy(attribute) {
      if (!attribute.attribute || attribute.sortable === false) {
        return;
      }
      if (this.sort === attribute.attribute && this.sortMode === "ASC") {
        this.sort = null;
        this.sortMode = "DESC";
      } else if (this.sort === attribute.attribute && this.sortMode === "DESC") {
        this.sortMode = "ASC";
      } else {
        this.sortMode = "DESC";
        this.sort = attribute.attribute;
      }
      const table = Object.assign({}, this.table);
      table.filter = unionBy([this.sortFilter], this.table.filter, "name");
      this.$store.dispatch('table/update', table);
    }
  },
  beforeDestroy() {
    this.$store.commit("table/delete", this.id);
    this.$bus.$off('refresh-table');
  },
  watch: {
    counter() {
      this.refresh()
    },
  }
};
</script>
